// Collapsible "Activity" section for the family workspace screen — a
// paginated, append-only feed of per-field changes for the family and its
// variants (BE: master_product_activity_logs). Lazy: `useGetFamilyActivityApi`
// only fires once the panel has been expanded at least once (mirrors
// family-suppliers-panel.tsx). Pages accumulate client-side behind a
// "Load more" button rather than replacing — this is a feed, not a
// paginated table.

import { FamilyActivityEntry, useGetFamilyActivityApi } from 'apis/master-product-families';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  HStack,
  Spinner,
  Tag,
  Text,
  Tooltip,
  VStack,
} from 'design-system';
import { useEffect, useState } from 'react';
import { formatActivityLine, sourceBadge } from './activity-format';

dayjs.extend(relativeTime);

export interface FamilyActivityPanelProps {
  familyId: number;
}

export const FamilyActivityPanel = ({ familyId }: FamilyActivityPanelProps) => {
  // Sticky once true, same rationale as the suppliers panel — an uncontrolled
  // Accordion collapsing/expanding again must never re-gate the query back off.
  const [hasExpanded, setHasExpanded] = useState(false);
  const [page, setPage] = useState(1);
  // Accumulated rows across pages. Remounted per family (key above), so state
  // is fresh each time.
  const [entries, setEntries] = useState<FamilyActivityEntry[]>([]);

  const { data, isLoading, isFetching } = useGetFamilyActivityApi(familyId, page, { enabled: hasExpanded });

  useEffect(() => {
    if (!data) return;
    setEntries((prev) => {
      // Page 1 always replaces outright (covers a background refetch of the
      // first page while the user hasn't paged further yet). Later pages
      // append, deduping by id in case `data` still reflects the previous
      // page for a render or two (keepPreviousData) before the new page
      // resolves — without the dedupe that stale render would re-append
      // rows already present in `prev`.
      if (page === 1) return data.data;
      const seenIds = new Set(prev.map((entry) => entry.id));
      const additions = data.data.filter((entry) => !seenIds.has(entry.id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [data, page]);

  const hasNext = data?.hasNext ?? false;
  const isFirstLoad = isLoading && entries.length === 0;

  return (
    <Accordion
      allowToggle
      onChange={(expandedIndex) => {
        if (expandedIndex !== -1) setHasExpanded(true);
      }}>
      <AccordionItem bg='white' border='1px solid' borderColor='gray.200' rounded='md'>
        <AccordionButton _expanded={{ bg: 'gray.50' }} py='3'>
          <AccordionIcon mr='2' />
          <Text flex='1' textAlign='left' fontWeight='semibold'>
            Activity
          </Text>
        </AccordionButton>
        <AccordionPanel pb='4'>
          {isFirstLoad ? (
            <Center py='4'>
              <Spinner size='sm' color='red.500' />
            </Center>
          ) : entries.length === 0 ? (
            <Text color='gray.500' fontSize='sm' py='2'>
              No activity yet.
            </Text>
          ) : (
            <VStack align='stretch' spacing='3' divider={<Box borderTopWidth='1px' borderColor='gray.100' />}>
              {entries.map((entry) => {
                const badge = sourceBadge(entry.source);
                const entityChipLabel = entry.entityType === 'family' ? 'Family' : entry.entityLabel;
                const authorName = entry.author?.name || 'System';
                const created = dayjs(entry.createdAt);

                return (
                  <Box key={entry.id} fontSize='sm'>
                    <HStack spacing='2' mb='1' flexWrap='wrap'>
                      <Tooltip label={created.format('MMM D, YYYY h:mm A')} hasArrow placement='top'>
                        <Text as='span' color='gray.500' fontSize='xs' cursor='default'>
                          {created.fromNow()}
                        </Text>
                      </Tooltip>
                      <Text as='span' color='gray.400' fontSize='xs'>
                        ·
                      </Text>
                      <Text as='span' fontWeight='medium' fontSize='xs'>
                        {authorName}
                      </Text>
                      <Tag size='sm' colorScheme='gray'>
                        {entityChipLabel}
                      </Tag>
                      {badge && (
                        <Tag size='sm' colorScheme='purple'>
                          {badge}
                        </Tag>
                      )}
                    </HStack>
                    <Text>{formatActivityLine(entry)}</Text>
                  </Box>
                );
              })}
            </VStack>
          )}

          {hasNext && !isFirstLoad && (
            <Center pt='3'>
              <Button size='sm' variant='outline' onClick={() => setPage((prev) => prev + 1)} isDisabled={isFetching}>
                {isFetching ? <Spinner size='xs' color='red.500' /> : 'Load more'}
              </Button>
            </Center>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
